import { useEffect, useMemo, useState } from "react";
import { Bot, CalendarClock, CheckCircle2, MessageSquareText, PencilLine, Send, Settings, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { properties } from "@/data/properties";

const normalizeText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const demoMessages = [
  "Buenos dias, sigue disponible el local?",
  "Hola, busco bodega en Culiacan para taller, presupuesto 25 mil",
  "Tengo un terreno en renta en Merida, quiero publicarlo",
  "Me interesa la propiedad 21, quiero poner una farmacia",
  "Te mando una foto del local",
];

const defaultFollowupRules = [
  {
    lead: "Lead caliente",
    time: "24 horas",
    message: "Hola, solo para dar seguimiento. Sigues interesado en revisar esta propiedad o agendar visita?",
  },
  {
    lead: "Lead medio",
    time: "48 horas",
    message: "Hola, te escribo para saber si ya tienes mas claro presupuesto, fecha o zona para ayudarte a filtrar mejores opciones.",
  },
  {
    lead: "Interesado inicial",
    time: "72 horas",
    message: "Hola, seguimos atentos por si quieres revisar opciones de locales, terrenos, oficinas o bodegas.",
  },
  {
    lead: "Dueno con datos faltantes",
    time: "24 horas",
    message: "Hola, te doy seguimiento para completar la informacion de tu propiedad.",
  },
];

const defaultFilterQuestions = [
  "[Interesado - busqueda] Que tipo de espacio necesitas: local, terreno, bodega, oficina o consultorio?",
  "[Interesado - ciudad] En que ciudad buscas?",
  "[Interesado - zona] En que zona o colonia especifica buscas?",
  "[Interesado - giro] Que giro quieres poner?",
  "[Interesado - presupuesto] Cual es tu presupuesto mensual aproximado?",
  "[Interesado - medidas] Cuantos metros aproximados necesitas?",
  "[Interesado - fecha] Para cuando necesitas ocupar el espacio?",
  "[Interesado - requisitos] Necesitas algun requisito especial como estacionamiento, esquina, avenida, planta baja o terraza?",
  "[Interesado - negocio] Ya tienes negocio operando o apenas lo vas a iniciar?",
  "[Interesado - visita] Te gustaria agendar visita o que te contacten para seguimiento?",
  "[Interesado - cierre] Si encuentro una propiedad con las condiciones que buscas, te la puedo enviar por aqui?",
  "[Propiedad especifica - giro] Que giro te gustaria poner en esta propiedad?",
  "[Propiedad especifica - fecha] Para cuando necesitas ocupar el espacio?",
  "[Propiedad especifica - presupuesto] Cual es tu presupuesto mensual aproximado?",
  "[Propiedad especifica - visita] Quieres que revisemos disponibilidad o agendemos visita?",
  "[Propiedad especifica - requisitos] Aceptas contrato minimo y requisitos basicos de renta?",
  "[Ambiguo - rentar] Quieres rentar tu propiedad o estas buscando rentar un espacio para tu negocio?",
  "[Ambiguo - intencion] Buscas comprar, rentar o publicar una propiedad?",
  "[Ambiguo - usuario] Me preguntas como dueno de una propiedad o como interesado en rentar?",
  "[Dueno - publicar] Que tipo de propiedad quieres publicar?",
  "[Dueno - operacion] La propiedad es en renta o venta?",
  "[Dueno - ubicacion] En que ciudad, colonia o zona esta?",
  "[Dueno - precio] Cual es el precio de renta o venta?",
  "[Dueno - medidas] Que medidas tiene?",
  "[Dueno - servicios] Que servicios tiene disponibles?",
  "[Dueno - disponibilidad] Cuando esta disponible?",
  "[Dueno - restricciones] Tiene alguna restriccion de giro o uso?",
  "[Dueno - fotos] Tienes fotos, videos o croquis de ubicacion?",
  "[Dueno - plan] Quieres publicar gratis o te interesa Plan Pro con analisis y leads perfilados?",
  "[Lead - presupuesto] Tu presupuesto ya esta definido o estas comparando opciones?",
  "[Lead - fecha] Tu fecha para ocupar es inmediata, en semanas o aun esta por definir?",
  "[Lead - documentacion] Cuentas con documentacion basica para renta?",
  "[Lead - requisitos] Tienes fiador, deposito o requisitos listos?",
  "[Lead - decision] Quien tomaria la decision final?",
  "[Cierre - seguimiento] Te puedo dar seguimiento por este medio?",
  "[Cierre - contacto] Prefieres que te contacte alguien para visita o primero quieres revisar opciones?",
];

const defaultAgentRules = [
  "Saludar en primer contacto o si el usuario regresa despues de varias horas/dias. En una conversacion activa, no repetir saludo.",
  "Si el usuario ya dio ciudad, tipo de espacio, giro, presupuesto o fecha, no volver a preguntar ese dato; preguntar solo lo que falta.",
  "Cuando el usuario busca un tipo de propiedad en una ciudad o zona, revisar primero si hay inventario posible en la base antes de pedir mas datos; no mostrar propiedad especifica hasta conocer giro y presupuesto.",
  "Pedir todos los datos importantes durante la conversacion, pero maximo dos preguntas por mensaje para no saturar al usuario.",
  "Si hay inventario exacto, decir que hay opciones para revisar y pedir los datos clave que falten.",
  "Si no hay inventario exacto, buscar opciones parecidas por tipo de espacio, ciudad cercana, zona o rango de precio; explicar que no es exacto y pedir giro + presupuesto para afinar.",
  "Si no hay ninguna opcion parecida, decirlo con claridad y ofrecer avisar cuando aparezca algo compatible, pidiendo giro, presupuesto, zonas y fecha.",
  "Para mostrar opciones finales, pedir ciudad y tambien zona o colonia especifica. Ciudad sola ayuda a orientar, pero es muy amplia para recomendar links.",
  "Cuando ya tenga tipo de espacio, ciudad, zona/colonia, giro y presupuesto, mostrar links de todas las propiedades que coincidan o las mas parecidas. La fecha ayuda, pero no debe bloquear mostrar opciones.",
  "Si muestra opciones y falta fecha, preguntar despues para cuando necesita ocupar sin detener la busqueda.",
  "Cuando ya existan varios datos del interesado, confirmar la informacion reunida con la plantilla de resumen antes de recomendar propiedades o pasar el lead.",
  "Si el usuario dice 'quiero rentar' sin aclarar, preguntar si quiere rentar su propiedad o si busca rentar un espacio para su negocio. No asumir intencion.",
  "Si el usuario pregunta precio, ubicacion o disponibilidad sin propiedad clara, pedir enlace, captura, nombre o numero de propiedad antes de responder datos especificos.",
  "Si el presupuesto es menor al precio de una propiedad, no descartarlo de golpe; ofrecer buscar opciones compatibles.",
  "Si el usuario es dueno y faltan precio o fotos, guardarlo como dueno prospecto y programar seguimiento.",
  "Si pregunta si puede poner cierto giro, responder con cautela: revisar restricciones, zona y condiciones; no garantizar.",
  "Si el usuario no responde datos importantes, hacer una sola pregunta simple y no insistir con muchas preguntas juntas.",
  "Si el usuario solo dice local, bodega, terreno u oficina, tratarlo como busqueda general y pedir zona + giro.",
  "No asumir una propiedad especifica si no manda ID, enlace, captura o nombre claro.",
  "Antes de pasar un lead al dueno o agendar visita, confirmar giro, presupuesto y fecha aproximada.",
  "Si el usuario tiene giro, presupuesto y fecha cercana, clasificar como lead caliente.",
  "Si falta presupuesto o fecha, clasificar como lead medio o inicial y dar seguimiento.",
  "Si el usuario manda imagen, preguntar primero si quiere publicar o si esta buscando.",
  "Siempre preguntar si acepta recibir opciones futuras antes de cerrar.",
];

const genericPropertyWords = new Set([
  "local",
  "locales",
  "terreno",
  "terrenos",
  "bodega",
  "bodegas",
  "oficina",
  "oficinas",
  "consultorio",
  "consultorios",
  "renta",
  "venta",
  "negocio",
  "negocios",
  "espacio",
  "espacios",
  "comercial",
  "comerciales",
  "tienes",
  "busco",
  "necesito",
  "quiero",
]);

const defaultResponseTemplates = {
  firstGreeting: "Hola, con gusto te ayudo.",
  returningGreeting: "Hola de nuevo, con gusto.",
  propertyInterest:
    "Con gusto te ayudo con {propertyTitle}. Para orientarte mejor, dime que giro te gustaria poner y para cuando necesitas ocupar el espacio.",
  unknownProperty:
    "Claro. Me puedes mandar el enlace, numero de propiedad, nombre o una captura para ubicarla?",
  propertySearch:
    "Claro, te ayudo a buscar opciones. Que tipo de espacio necesitas y en que ciudad o zona te interesa?",
  ownerPublish:
    "Perfecto, te puedo ayudar a publicar tu propiedad en Tulocalmx. Para empezar, dime que tipo de propiedad es, en que ciudad esta y si es renta o venta.",
  imageReceived:
    "Gracias, ya recibi la imagen. Para ayudarte mejor, dime si es de una propiedad que quieres publicar o de una propiedad que estas buscando.",
  leadSummary:
    "Perfecto, entonces buscas:\n\nTipo de espacio: {spaceType}\nMedidas: {sizeRange}\nPresupuesto: {budget}\nZonas: {zones}\nGiro: {businessType}\nFecha: {moveInDate}\n\nCon esta informacion puedo revisar opciones mas compatibles.",
  unknown:
    "Hola, soy el asesor de Tulocalmx. Te puedo ayudar a buscar un espacio comercial o a publicar una propiedad. Que necesitas revisar?",
};

const defaultCustomResponseTemplates = [
  {
    id: "ambiguousRent",
    label: "Intencion ambigua: quiero rentar",
    template:
      "Quieres rentar tu propiedad o estas buscando rentar un espacio para tu negocio?",
  },
  {
    id: "priceWithoutProperty",
    label: "Pregunta por precio sin propiedad clara",
    template:
      "Claro. Sobre que propiedad me preguntas? Puedes mandarme el enlace, nombre, numero de propiedad o una captura para ubicarla y revisar el precio.",
  },
  {
    id: "locationRequest",
    label: "Pregunta por ubicacion",
    template:
      "Con gusto te paso la ubicacion. Para confirmar que sea la correcta, me compartes el enlace, nombre o numero de la propiedad?",
  },
  {
    id: "businessAllowed",
    label: "Pregunta por giro permitido",
    template:
      "Podemos revisarlo con cuidado. Para decirte si tu giro puede tener sentido, necesito confirmar restricciones de la propiedad, zona, servicios y condiciones del espacio.",
  },
  {
    id: "budgetNotCompatible",
    label: "Presupuesto no compatible",
    template:
      "Esta propiedad puede estar arriba de ese presupuesto, pero puedo ayudarte a buscar opciones mas compatibles con el rango que tienes.",
  },
  {
    id: "ownerSummary",
    label: "Resumen para enviar al dueno",
    template:
      "Nuevo lead perfilado:\n\nNombre: {name}\nTelefono: {phone}\nPropiedad: {propertyTitle}\nGiro: {businessType}\nPresupuesto: {budget}\nFecha estimada: {moveInDate}\nZona de interes: {zones}\nRequisitos especiales: {specialRequirements}\nNivel del lead: {leadStatus}\nResumen: {summary}\nSiguiente accion sugerida: {nextAction}",
  },
  {
    id: "futureOptionsClose",
    label: "Cierre con opcion futura",
    template:
      "Si encuentro una propiedad con las condiciones que buscas, te la puedo enviar por aqui?",
  },
  {
    id: "inventoryFoundNoShow",
    label: "Hay inventario pero faltan datos",
    template:
      "Si tenemos opciones que pueden revisarse para {spaceType} en {city}. Antes de mostrarte una propiedad, dime que giro quieres poner y cual es tu presupuesto aproximado.",
  },
  {
    id: "inventoryNotFound",
    label: "No hay inventario exacto",
    template:
      "Por ahora no veo una opcion publicada exactamente para {spaceType} en {city}. Puedo revisar opciones parecidas en otras zonas o con caracteristicas cercanas. Para afinar, dime que giro quieres poner y cual es tu presupuesto aproximado. Si aparece algo compatible, te lo puedo enviar por aqui.",
  },
  {
    id: "similarInventoryFound",
    label: "Hay opciones parecidas",
    template:
      "No veo algo exactamente igual para {spaceType} en {city}, pero si hay opciones parecidas que podrian revisarse. Para filtrar mejor, dime que giro quieres poner y cual es tu presupuesto aproximado.",
  },
];

const AGENT_PANEL_STORAGE_KEY = "tulocalmx:whatsapp-agent-panel:v1";

const readSavedAgentPanel = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(localStorage.getItem(AGENT_PANEL_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const mergeTextDefaults = (savedItems = [], defaultItems = []) => {
  const merged = Array.isArray(savedItems) ? [...savedItems] : [];
  const normalizedExisting = new Set(merged.map((item) => normalizeText(item)));

  defaultItems.forEach((item) => {
    if (!normalizedExisting.has(normalizeText(item))) {
      merged.push(item);
    }
  });

  return merged;
};

const mergeCustomTemplates = (savedItems = [], defaultItems = []) => {
  const merged = Array.isArray(savedItems) ? [...savedItems] : [];
  const existingIds = new Set(merged.map((item) => item.id));

  defaultItems.forEach((item) => {
    if (!existingIds.has(item.id)) {
      merged.push(item);
    }
  });

  return merged;
};

const applyTemplate = (template, values = {}) =>
  Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value || ""),
    template
  );

const getDetectedCity = (text) => {
  const cityPatterns = [
    ["Culiacan", /culiacan|culiacán/],
    ["Merida", /merida|mérida/],
    ["Ciudad de Mexico", /ciudad de mexico|cdmx|coyoacan|coyoacán/],
    ["Guadalajara", /guadalajara/],
    ["Monterrey", /monterrey/],
  ];
  return cityPatterns.find(([, pattern]) => pattern.test(text))?.[0] || "";
};

const getDetectedZone = (text) => {
  const zonePatterns = [
    ["Stanza Cantabria", /stanza|cantabria|ampuero/],
    ["Cholul", /cholul|san pedro cholul/],
    ["Sitpach", /sitpach/],
    ["Culiacancito", /culiacancito/],
    ["La Costerita", /costerita/],
    ["Malecon Viejo", /malecon|malecÃ³n/],
    ["Tres Rios", /tres rios|tres rÃ­os/],
    ["Coyoacan", /coyoacan|coyoacÃ¡n|auriga/],
    ["Las Quintas", /las quintas/],
    ["Blvd Conquistadores", /conquistadores/],
    ["Valle Alto", /valle alto/],
  ];
  return zonePatterns.find(([, pattern]) => pattern.test(text))?.[0] || "";
};

const getDetectedSpaceType = (text) => {
  const spacePatterns = [
    ["bodega", /bodega|bodegas/],
    ["local", /local|locales/],
    ["terreno", /terreno|terrenos/],
    ["oficina", /oficina|oficinas/],
    ["consultorio", /consultorio|consultorios/],
  ];
  return spacePatterns.find(([, pattern]) => pattern.test(text))?.[0] || "";
};

const propertyMatchesSpaceType = (property, detectedSpaceType) => {
  if (!detectedSpaceType) return true;
  const haystack = normalizeText(
    [property.type, property.title, property.category, property.categoria].filter(Boolean).join(" ")
  );

  if (detectedSpaceType === "local") return haystack.includes("local");
  if (detectedSpaceType === "terreno") return haystack.includes("terreno");
  if (detectedSpaceType === "bodega") return haystack.includes("bodega");
  if (detectedSpaceType === "oficina") return haystack.includes("oficina");
  if (detectedSpaceType === "consultorio") return haystack.includes("consultorio") || haystack.includes("oficina");
  return haystack.includes(normalizeText(detectedSpaceType));
};

const propertyMatchesCity = (property, detectedCity) => {
  if (!detectedCity) return true;
  const haystack = normalizeText(
    [
      property.city,
      property.ciudad,
      property.municipio,
      property.alcaldia,
      property.state,
      property.entidad,
      property.address,
      property.location,
      property.zona,
      property.title,
    ]
      .filter(Boolean)
      .join(" ")
  );
  return haystack.includes(normalizeText(detectedCity));
};

const propertyMatchesZone = (property, detectedZone) => {
  if (!detectedZone) return true;
  const haystack = normalizeText(
    [
      property.address,
      property.location,
      property.zona,
      property.title,
      property.description,
      property.descripcion,
    ]
      .filter(Boolean)
      .join(" ")
  );
  return haystack.includes(normalizeText(detectedZone));
};

const findInventoryMatches = ({ detectedSpaceType, detectedCity, detectedZone }) =>
  properties
    .filter((property) => propertyMatchesSpaceType(property, detectedSpaceType))
    .filter((property) => propertyMatchesCity(property, detectedCity))
    .filter((property) => propertyMatchesZone(property, detectedZone));

const findSimilarInventoryMatches = ({ detectedSpaceType, detectedCity, detectedZone }) =>
  properties.filter((property) => {
    const matchesType = propertyMatchesSpaceType(property, detectedSpaceType);
    const matchesCity = propertyMatchesCity(property, detectedCity);
    const matchesZone = propertyMatchesZone(property, detectedZone);
    return matchesType || matchesCity || matchesZone;
  });

const getCustomTemplate = (id, fallback, customTemplates = []) =>
  customTemplates.find((item) => item.id === id)?.template || fallback;

const getPropertyUrl = (property) => {
  const link = property.link || `/propiedad/${property.id}`;
  return link.startsWith("http") ? link : `https://tulocalmx.mx${link}`;
};

const formatPropertyPrice = (property) => {
  if (property.price === null || property.price === undefined || property.price === "") {
    return "Renta por confirmar";
  }

  if (typeof property.price === "number") {
    return `$${property.price.toLocaleString("es-MX")}`;
  }

  return String(property.price);
};

const formatPropertyZone = (property) =>
  [
    property.address,
    property.location,
    property.city || property.ciudad || property.municipio || property.alcaldia,
  ]
    .filter(Boolean)
    .join(" | ");

const formatPropertyOptionsReply = ({ matches, similar = false }) => {
  const intro = similar
    ? `No veo una opcion exacta, pero encontre ${matches.length} opcion${matches.length === 1 ? "" : "es"} parecida${matches.length === 1 ? "" : "s"} que pueden revisarse:`
    : `Con los datos que me diste, encontre ${matches.length} opcion${matches.length === 1 ? "" : "es"} que pueden coincidir:`;

  const list = matches
    .map((property, index) => {
      const title = property.title || `Propiedad ${property.id}`;
      const zone = formatPropertyZone(property) || "Zona por confirmar";
      return `${index + 1}. ${title}\nRenta: ${formatPropertyPrice(property)}\nZona: ${zone}\nLink: ${getPropertyUrl(property)}`;
    })
    .join("\n\n");

  return `${intro}\n\n${list}\n\nLa fecha no bloquea revisar opciones, pero para perfilar mejor: para cuando te gustaria ocupar?`;
};

const getSearchReply = ({ text, responseTemplates, conversationState, customTemplates = [] }) => {
  const detectedCity = getDetectedCity(text);
  const detectedZone = getDetectedZone(text);
  const detectedSpaceType = getDetectedSpaceType(text);
  const hasBudget = /\b\d{2,}|\bmil\b|\$/.test(text);
  const hasBusiness =
    /(farmacia|taller|restaurante|consultorio|abarrotes|oficina|bodega|ferreteria|comida|barberia|gimnasio)/.test(text);
  const hasDate = /(hoy|manana|semana|mes|agosto|septiembre|octubre|noviembre|diciembre|enero|febrero|marzo|abril|mayo|junio|julio)/.test(text);
  const inventoryMatches = findInventoryMatches({ detectedSpaceType, detectedCity, detectedZone });
  const similarInventoryMatches = findSimilarInventoryMatches({ detectedSpaceType, detectedCity, detectedZone });
  const hasMinimumDataForOptions = Boolean(
    detectedSpaceType && detectedCity && detectedZone && hasBusiness && hasBudget
  );

  const missing = [
    !detectedSpaceType ? "que tipo de espacio necesitas" : "",
    !detectedCity ? "en que ciudad buscas" : "",
    !detectedZone ? "en que zona o colonia especifica buscas" : "",
    !hasBusiness ? "que giro quieres poner" : "",
    !hasBudget ? "cual es tu presupuesto aproximado" : "",
    !hasDate ? "para cuando necesitas ocupar" : "",
  ].filter(Boolean);

  if (hasMinimumDataForOptions) {
    if (inventoryMatches.length) {
      return withGreeting(
        formatPropertyOptionsReply({ matches: inventoryMatches, similar: false }),
        conversationState,
        responseTemplates
      );
    }

    if (similarInventoryMatches.length) {
      return withGreeting(
        formatPropertyOptionsReply({ matches: similarInventoryMatches, similar: true }),
        conversationState,
        responseTemplates
      );
    }

    return withGreeting(
      "Por ahora no veo opciones publicadas compatibles con esos datos. Si estas de acuerdo, guardo tu busqueda y te aviso cuando aparezca algo parecido. Tambien puedo revisar si aceptas otras zonas o presupuestos cercanos.",
      conversationState,
      responseTemplates
    );
  }

  if (detectedSpaceType && detectedCity && !detectedZone) {
    const secondQuestion = hasBudget
      ? "quieres mantener ese mismo presupuesto"
      : "cual es tu presupuesto aproximado";
    return withGreeting(
      `Veo que buscas ${detectedSpaceType} en ${detectedCity}. Para darte opciones mas utiles, dime en que zona o colonia especifica buscas y ${secondQuestion}.`,
      conversationState,
      responseTemplates
    );
  }

  if (detectedSpaceType && detectedCity && (!hasBusiness || !hasBudget)) {
    const templateId = inventoryMatches.length
      ? "inventoryFoundNoShow"
      : similarInventoryMatches.length
        ? "similarInventoryFound"
        : "inventoryNotFound";
    const fallback = inventoryMatches.length
      ? "Si tenemos opciones que pueden revisarse para {spaceType} en {city}. Antes de mostrarte una propiedad, dime que giro quieres poner y cual es tu presupuesto aproximado."
      : similarInventoryMatches.length
        ? "No veo algo exactamente igual para {spaceType} en {city}, pero si hay opciones parecidas que podrian revisarse. Para filtrar mejor, dime que giro quieres poner y cual es tu presupuesto aproximado."
        : "Por ahora no veo una opcion publicada exactamente para {spaceType} en {city}. Puedo revisar opciones parecidas en otras zonas o con caracteristicas cercanas. Para afinar, dime que giro quieres poner y cual es tu presupuesto aproximado.";
    const template = getCustomTemplate(templateId, fallback, customTemplates);
    return withGreeting(
      applyTemplate(template, {
        spaceType: detectedSpaceType,
        city: detectedCity,
      }),
      conversationState,
      responseTemplates
    );
  }

  if (missing.length === 0) {
    return withGreeting(
      "Ya tengo una buena base de lo que buscas. Te puedo ayudar a revisar opciones compatibles y, si aparece una propiedad con esas condiciones, te la puedo enviar por aqui.",
      conversationState,
      responseTemplates
    );
  }

  const nextQuestions = missing.slice(0, 2).join(" y ");
  const context = [
    detectedSpaceType ? `buscas ${detectedSpaceType}` : "",
    detectedCity ? `en ${detectedCity}` : "",
    detectedZone ? `zona ${detectedZone}` : "",
    hasBudget ? "con presupuesto aproximado indicado" : "",
  ]
    .filter(Boolean)
    .join(", ");

  const prefix = context
    ? `Veo que ${context}.`
    : "Claro, te ayudo a buscar opciones.";

  return withGreeting(
    `${prefix} Para avanzar, dime ${nextQuestions}.`,
    conversationState,
    responseTemplates
  );
};

const shouldGreet = ({ hasGreeted = false, hoursSinceLastMessage = 0 }) =>
  !hasGreeted || Number(hoursSinceLastMessage || 0) >= 8;

const capitalizeSentenceStart = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const withGreeting = (reply, conversationState, responseTemplates) => {
  if (!shouldGreet(conversationState)) return reply;
  const greeting = conversationState.hasGreeted
    ? responseTemplates.returningGreeting || "Hola de nuevo, con gusto."
    : responseTemplates.firstGreeting || "Hola, con gusto te ayudo.";
  const cleanReply = String(reply || "")
    .replace(/^(hola|buenos dias|buenas tardes|buenas noches),?\s+/i, "")
    .replace(/^(perfecto|claro),?\s+/i, "");
  return `${greeting} ${capitalizeSentenceStart(cleanReply)}`.replace(/\s+/g, " ").trim();
};

const detectProperty = (text) => {
  const normalized = normalizeText(text);
  const idMatch = normalized.match(/propiedad\s*(\d+)/);
  if (idMatch) {
    return properties.find((property) => String(property.id) === idMatch[1]) || null;
  }

  return (
    properties.find((property) => {
      const haystack = normalizeText(
        [property.title, property.address, property.location, property.type].filter(Boolean).join(" ")
      );
      const meaningfulWords = normalized
        .split(/\s+/)
        .filter((word) => word.length > 4)
        .filter((word) => !genericPropertyWords.has(word));

      if (meaningfulWords.length < 2) return false;
      return meaningfulWords.filter((word) => haystack.includes(word)).length >= 2;
    }) || null
  );
};

const hasAmbiguousRentalIntent = (text) =>
  /\brentar\s+(un|una|el|la)?\s*(local|locales|bodega|bodegas|terreno|terrenos|casa|casas|oficina|oficinas|consultorio|consultorios|espacio|espacios|propiedad|propiedades)\b/.test(text);

const findCorrection = (message, corrections = []) => {
  const normalized = normalizeText(message);
  return corrections.find((correction) => normalizeText(correction.userMessage) === normalized);
};

const analyzeMessage = (
  message,
  responseTemplates = defaultResponseTemplates,
  customTemplates = [],
  corrections = [],
  conversationState = { hasGreeted: false }
) => {
  const savedCorrection = findCorrection(message, corrections);
  if (savedCorrection) {
    return {
      intent: savedCorrection.intent || "corrected_response",
      leadStatus: savedCorrection.leadStatus || "initial",
      reply: savedCorrection.correctedReply,
      missingFields: savedCorrection.missingFields || [],
      followup: savedCorrection.followup || "Segun correccion guardada",
      property: null,
      shouldNotifyOwner: false,
      usedCorrection: true,
    };
  }

  const text = normalizeText(message);
  const property = detectProperty(message);
  const hasBudget = /\b\d{2,}|\bmil\b|\$/.test(text);
  const hasBusiness =
    /(farmacia|taller|restaurante|consultorio|abarrotes|oficina|bodega|ferreteria|comida|barberia|gimnasio)/.test(text);
  const hasDate = /(hoy|manana|semana|mes|agosto|septiembre|octubre|noviembre|diciembre|enero|febrero|marzo|abril|mayo|junio|julio)/.test(text);
  const wantsVisit = /(visita|verla|verlo|agendar|cita|contactar|llamar)/.test(text);
  const image = /(foto|imagen|captura|mando|envio)/.test(text);
  const owner = /(tengo|publicar|subir|rentar mi|vender mi|propiedad en renta|terreno en renta|local en renta)/.test(text);
  const spaceTypeMentioned = /(local|bodega|terreno|casa|oficina|consultorio|espacio|plaza|locales|bodegas|terrenos|casas|oficinas|consultorios)/.test(text);
  const search = /(busco|necesito|tienes|quiero rentar|quiero buscar|me interesa)/.test(text) && spaceTypeMentioned;
  const looseQuestion = /(cuanto|precio|donde|ubicacion|estacionamiento|disponible|sigue disponible)/.test(text);

  if (image) {
    return {
      intent: "image_received",
      leadStatus: "initial",
      reply: withGreeting(responseTemplates.imageReceived, conversationState, responseTemplates),
      missingFields: ["confirmar si busca o publica", "giro o datos de propiedad"],
      followup: "Sin seguimiento todavia",
      property,
      shouldNotifyOwner: false,
    };
  }

  if (hasAmbiguousRentalIntent(text)) {
    return {
      intent: "ambiguous_rent",
      leadStatus: "initial",
      reply: withGreeting(
        getCustomTemplate(
          "ambiguousRent",
          "Estas buscando rentar un espacio para tu negocio o tienes una propiedad que quieres rentar?",
          customTemplates
        ),
        conversationState,
        responseTemplates
      ),
      missingFields: ["confirmar si busca rentar o publicar propiedad"],
      followup: "Sin seguimiento todavia",
      property: null,
      shouldNotifyOwner: false,
    };
  }

  if (owner) {
    return {
      intent: "owner_publish",
      leadStatus: "owner_prospect",
      reply: withGreeting(responseTemplates.ownerPublish, conversationState, responseTemplates),
      missingFields: ["ubicacion", "precio", "medidas", "fotos"],
      followup: "24 horas si faltan datos",
      property: null,
      shouldNotifyOwner: false,
    };
  }

  if (property || looseQuestion) {
    const hot = hasBusiness && (hasBudget || hasDate || wantsVisit);
    return {
      intent: "property_interest",
      leadStatus: hot ? "hot" : "initial",
      reply: property
        ? withGreeting(
            applyTemplate(responseTemplates.propertyInterest, { propertyTitle: property.title }),
            conversationState,
            responseTemplates
          )
        : withGreeting(responseTemplates.unknownProperty, conversationState, responseTemplates),
      missingFields: [
        !hasBusiness ? "giro" : "",
        !hasBudget ? "presupuesto" : "",
        !hasDate ? "fecha para ocupar" : "",
      ].filter(Boolean),
      followup: hot ? "24 horas" : "72 horas si acepta recibir opciones",
      property,
      shouldNotifyOwner: hot,
    };
  }

  if (search) {
    const medium = hasBusiness || hasBudget;
    return {
      intent: "property_search",
      leadStatus: medium ? "medium" : "initial",
      reply: getSearchReply({ text, responseTemplates, conversationState, customTemplates }),
      missingFields: [
        !hasBusiness ? "giro" : "",
        !hasBudget ? "presupuesto" : "",
        !hasDate ? "fecha para ocupar (opcional)" : "",
        !getDetectedCity(text) ? "ciudad" : "",
        !getDetectedZone(text) ? "zona o colonia especifica" : "",
        !getDetectedSpaceType(text) ? "tipo de espacio" : "",
      ].filter(Boolean),
      followup: medium ? "48 horas" : "72 horas si acepta recibir opciones",
      property: null,
      shouldNotifyOwner: false,
    };
  }

  return {
    intent: "unknown",
    leadStatus: "unqualified",
    reply: withGreeting(responseTemplates.unknown, conversationState, responseTemplates),
    missingFields: ["intencion"],
    followup: "Sin seguimiento todavia",
    property: null,
    shouldNotifyOwner: false,
  };
};

const statusStyles = {
  hot: "border-emerald-200 bg-emerald-50 text-emerald-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  initial: "border-blue-200 bg-blue-50 text-blue-800",
  owner_prospect: "border-orange-200 bg-orange-50 text-orange-800",
  unqualified: "border-slate-200 bg-slate-50 text-slate-700",
};

const AdminAgenteWhatsApp = () => {
  const navigate = useNavigate();
  const savedPanel = useMemo(() => readSavedAgentPanel(), []);
  const [message, setMessage] = useState(demoMessages[0]);
  const [agentName, setAgentName] = useState(savedPanel.agentName || "Agente Tulocalmx");
  const [tone, setTone] = useState(
    savedPanel.tone || "Profesional, amable, claro, comercial y no insistente."
  );
  const [maxQuestions, setMaxQuestions] = useState(savedPanel.maxQuestions || 2);
  const [hasGreeted, setHasGreeted] = useState(Boolean(savedPanel.hasGreeted));
  const [hoursSinceLastMessage, setHoursSinceLastMessage] = useState(
    savedPanel.hoursSinceLastMessage || 0
  );
  const [responseTemplates, setResponseTemplates] = useState({
    ...defaultResponseTemplates,
    ...(savedPanel.responseTemplates || {}),
  });
  const [customResponseTemplates, setCustomResponseTemplates] = useState(
    mergeCustomTemplates(savedPanel.customResponseTemplates, defaultCustomResponseTemplates)
  );
  const [editableRules, setEditableRules] = useState(
    mergeTextDefaults(savedPanel.agentRules, defaultAgentRules)
  );
  const [editableQuestions, setEditableQuestions] = useState(
    mergeTextDefaults(savedPanel.filterQuestions, defaultFilterQuestions)
  );
  const [editableFollowups, setEditableFollowups] = useState(
    savedPanel.followupRules || defaultFollowupRules
  );
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctedReply, setCorrectedReply] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [corrections, setCorrections] = useState(savedPanel.corrections || []);
  const [savedNotice, setSavedNotice] = useState("");

  const result = useMemo(
    () =>
      analyzeMessage(message, responseTemplates, customResponseTemplates, corrections, {
        hasGreeted,
        hoursSinceLastMessage,
      }),
    [message, responseTemplates, customResponseTemplates, corrections, hasGreeted, hoursSinceLastMessage]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const payload = {
      agentName,
      tone,
      maxQuestions,
      hasGreeted,
      hoursSinceLastMessage,
      responseTemplates,
      customResponseTemplates,
      agentRules: editableRules,
      filterQuestions: editableQuestions,
      followupRules: editableFollowups,
      corrections,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(AGENT_PANEL_STORAGE_KEY, JSON.stringify(payload));
    setSavedNotice("Guardado en este navegador");
    const timeout = window.setTimeout(() => setSavedNotice(""), 1800);
    return () => window.clearTimeout(timeout);
  }, [
    agentName,
    tone,
    maxQuestions,
    hasGreeted,
    hoursSinceLastMessage,
    responseTemplates,
    customResponseTemplates,
    editableRules,
    editableQuestions,
    editableFollowups,
    corrections,
  ]);

  const updateTemplate = (key, value) => {
    setResponseTemplates((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const addCustomResponseTemplate = () => {
    setCustomResponseTemplates((current) => [
      ...current,
      {
        id: `custom-${Date.now()}`,
        label: "Nueva respuesta base",
        template: "Escribe aqui la respuesta base.",
      },
    ]);
  };

  const updateCustomResponseTemplate = (index, field, value) => {
    setCustomResponseTemplates((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const removeCustomResponseTemplate = (index) => {
    setCustomResponseTemplates((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const updateRule = (index, value) => {
    setEditableRules((current) =>
      current.map((rule, ruleIndex) => (ruleIndex === index ? value : rule))
    );
  };

  const addRule = () => {
    setEditableRules((current) => [
      ...current,
      "Nueva regla: describe aqui el comportamiento que debe seguir el agente.",
    ]);
  };

  const removeRule = (index) => {
    setEditableRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const updateQuestion = (index, value) => {
    setEditableQuestions((current) =>
      current.map((question, questionIndex) => (questionIndex === index ? value : question))
    );
  };

  const addQuestion = () => {
    setEditableQuestions((current) => [...current, "Nueva pregunta del filtro"]);
  };

  const removeQuestion = (index) => {
    setEditableQuestions((current) =>
      current.filter((_, questionIndex) => questionIndex !== index)
    );
  };

  const updateFollowup = (index, field, value) => {
    setEditableFollowups((current) =>
      current.map((rule, ruleIndex) =>
        ruleIndex === index
          ? {
              ...rule,
              [field]: value,
            }
          : rule
      )
    );
  };

  const addFollowup = () => {
    setEditableFollowups((current) => [
      ...current,
      {
        lead: "Nuevo seguimiento",
        time: "24 horas",
        message: "Escribe aqui el mensaje de seguimiento.",
      },
    ]);
  };

  const removeFollowup = (index) => {
    setEditableFollowups((current) =>
      current.filter((_, followupIndex) => followupIndex !== index)
    );
  };

  const openCorrectionForm = () => {
    setCorrectedReply(result.reply);
    setCorrectionReason("");
    setShowCorrectionForm(true);
  };

  const saveCorrection = () => {
    if (!correctedReply.trim()) return;
    setCorrections((current) => [
      {
        id: Date.now(),
        userMessage: message,
        wrongReply: result.reply,
        correctedReply: correctedReply.trim(),
        reason: correctionReason.trim(),
        intent: result.intent,
        leadStatus: result.leadStatus,
        missingFields: result.missingFields,
        followup: result.followup,
      },
      ...current,
    ]);
    setShowCorrectionForm(false);
  };

  const resetLocalChanges = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(AGENT_PANEL_STORAGE_KEY);
    }
    setAgentName("Agente Tulocalmx");
    setTone("Profesional, amable, claro, comercial y no insistente.");
    setMaxQuestions(2);
    setHasGreeted(false);
    setHoursSinceLastMessage(0);
    setResponseTemplates(defaultResponseTemplates);
    setCustomResponseTemplates(defaultCustomResponseTemplates);
    setEditableRules(defaultAgentRules);
    setEditableQuestions(defaultFilterQuestions);
    setEditableFollowups(defaultFollowupRules);
    setCorrections([]);
    setSavedNotice("Cambios locales reiniciados");
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8">
      <section className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-orange-600">
              Agente WhatsApp
            </p>
            <h1 className="mt-1 text-2xl font-black text-slate-950">
              Simulador y estructura del agente
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Prueba conversaciones antes de conectar WhatsApp real. Esta primera version
              ayuda a revisar tono, filtros, clasificacion y seguimientos sin generar costo de OpenAI.
            </p>
            <p className="mt-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
              Los cambios se guardan automaticamente en este navegador
            </p>
          </div>
          <div className="flex items-center gap-2">
            {savedNotice && (
              <span className="hidden rounded-full bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 sm:inline-flex">
                {savedNotice}
              </span>
            )}
            <button
              type="button"
              onClick={() => navigate("/admin/agente-whatsapp/conversaciones")}
              className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-black text-blue-700"
            >
              Ver conversaciones
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
              aria-label="Salir"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="mb-6 grid gap-3 sm:grid-cols-4">
          {[
            ["Conversaciones", "whatsapp_conversations"],
            ["Mensajes", "whatsapp_messages"],
            ["Leads", "whatsapp_leads"],
            ["Seguimientos", "whatsapp_followups"],
          ].map(([label, table]) => (
            <div key={table} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
              <p className="mt-1 break-words text-sm font-black text-slate-950">{table}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                    <MessageSquareText size={21} />
                  </span>
                  <div>
                    <h2 className="text-lg font-black text-slate-950">Probar mensaje</h2>
                    <p className="text-sm text-slate-600">
                      Escribe como si fuera un WhatsApp real.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-3 flex flex-wrap gap-2">
                  {demoMessages.map((sample) => (
                    <button
                      key={sample}
                      type="button"
                      onClick={() => setMessage(sample)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                    >
                      {sample}
                    </button>
                  ))}
                </div>

                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={5}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:bg-white"
                />

                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 text-sm font-black text-blue-900">
                      <Bot size={18} />
                      Respuesta sugerida
                      {result.usedCorrection && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black text-emerald-800">
                          correccion aplicada
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={openCorrectionForm}
                      className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-700 shadow-sm"
                    >
                      Corregir respuesta
                    </button>
                  </div>
                  <p className="text-sm leading-6 text-blue-950">{result.reply}</p>
                </div>

                {showCorrectionForm && (
                  <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4">
                    <div className="mb-3">
                      <p className="text-sm font-black text-orange-900">Guardar correccion</p>
                      <p className="mt-1 text-xs font-bold leading-5 text-orange-800">
                        Esto corrige este caso en el simulador. Despues lo conectamos a Supabase para que quede guardado en `whatsapp_agent_corrections`.
                      </p>
                    </div>
                    <label className="block text-xs font-black uppercase text-orange-900">
                      Respuesta correcta
                    </label>
                    <textarea
                      value={correctedReply}
                      onChange={(event) => setCorrectedReply(event.target.value)}
                      rows={4}
                      className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
                    />
                    <label className="mt-3 block text-xs font-black uppercase text-orange-900">
                      Regla o motivo de la correccion
                    </label>
                    <textarea
                      value={correctionReason}
                      onChange={(event) => setCorrectionReason(event.target.value)}
                      rows={2}
                      placeholder="Ej: Si solo dice local, no asumir propiedad; pedir ciudad y giro."
                      className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={saveCorrection}
                        className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-black text-white"
                      >
                        Guardar correccion
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCorrectionForm(false)}
                        className="rounded-lg border border-orange-200 bg-white px-4 py-2 text-sm font-black text-orange-700"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-lg font-black text-slate-950">Resultado del agente</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Intencion</p>
                  <p className="mt-1 text-base font-black text-slate-950">{result.intent}</p>
                </div>
                <div className={`rounded-xl border p-4 ${statusStyles[result.leadStatus] || statusStyles.unqualified}`}>
                  <p className="text-xs font-bold uppercase">Nivel del lead</p>
                  <p className="mt-1 text-base font-black">{result.leadStatus}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Seguimiento</p>
                  <p className="mt-1 text-sm font-black text-slate-950">{result.followup}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Avisar al dueno</p>
                  <p className="mt-1 flex items-center gap-2 text-sm font-black text-slate-950">
                    {result.shouldNotifyOwner ? <CheckCircle2 size={16} /> : <X size={16} />}
                    {result.shouldNotifyOwner ? "Si, posible lead perfilado" : "Todavia no"}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Datos faltantes</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {result.missingFields.length ? (
                      result.missingFields.map((field) => (
                        <span key={field} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
                          {field}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm font-bold text-emerald-700">Datos principales completos</span>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Propiedad detectada</p>
                  {result.property ? (
                    <div className="mt-2">
                      <p className="font-black text-slate-950">{result.property.title}</p>
                      <p className="mt-1 text-sm text-slate-600">ID {result.property.id}</p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm font-semibold text-slate-600">Sin propiedad especifica</p>
                  )}
                </div>
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <CheckCircle2 size={20} className="text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-black text-slate-950">Reglas del agente</h2>
                  <p className="text-sm text-slate-600">
                    Estas reglas evitan que tengas que escribir miles de respuestas.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addRule}
                  className="shrink-0 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-2 text-xs font-black text-white shadow-sm"
                >
                  + Agregar regla
                </button>
              </div>
              <div className="space-y-2">
                {editableRules.map((rule, index) => (
                  <div key={`rule-${index}`} className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="text-xs font-black uppercase text-emerald-700">Regla {index + 1}</p>
                      <button
                        type="button"
                        onClick={() => removeRule(index)}
                        className="text-xs font-black text-emerald-700"
                      >
                        Quitar
                      </button>
                    </div>
                    <textarea
                      value={rule}
                      onChange={(event) => updateRule(index, event.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-emerald-100 bg-white px-3 py-2 text-sm font-semibold leading-5 text-emerald-950 outline-none focus:border-emerald-300"
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">Donde editar cada cosa</h2>
              <div className="mt-3 grid gap-2">
                {[
                  ["Regla", "Comportamiento general: no repetir datos, saludar segun tiempo, no asumir propiedad."],
                  ["Respuesta base", "Texto principal que dice el agente para una situacion comun."],
                  ["Pregunta del filtro", "Dato que quieres obtener: giro, presupuesto, zona, fecha, medidas."],
                  ["Seguimiento", "Mensaje y tiempo para volver a escribir despues."],
                  ["Correccion", "Caso especifico donde el agente respondio mal y tu corriges esa respuesta."],
                ].map(([type, description]) => (
                  <div key={type} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <p className="text-xs font-black uppercase text-slate-500">{type}</p>
                    <p className="mt-1 text-sm font-semibold leading-5 text-slate-700">{description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <PencilLine size={20} className="text-orange-600" />
                <div>
                  <h2 className="font-black text-slate-950">Correcciones guardadas</h2>
                  <p className="text-sm text-slate-600">
                    Casos reales que usara el simulador en esta sesion.
                  </p>
                </div>
              </div>
              {corrections.length ? (
                <div className="space-y-3">
                  {corrections.map((correction) => (
                    <div key={correction.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-black uppercase text-slate-400">Mensaje</p>
                      <p className="text-sm font-bold text-slate-900">{correction.userMessage}</p>
                      <p className="mt-2 text-xs font-black uppercase text-slate-400">Respuesta correcta</p>
                      <p className="text-sm leading-5 text-slate-700">{correction.correctedReply}</p>
                      {correction.reason && (
                        <>
                          <p className="mt-2 text-xs font-black uppercase text-slate-400">Regla</p>
                          <p className="text-xs leading-5 text-slate-600">{correction.reason}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm font-bold leading-5 text-slate-600">
                  Aun no hay correcciones. Prueba un mensaje y usa el boton "Corregir respuesta".
                </p>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                  <Settings size={20} />
                </span>
                <div>
                  <h2 className="font-black text-slate-950">Configuracion visual</h2>
                  <p className="text-sm text-slate-600">Despues esto se guardara en Supabase.</p>
                </div>
              </div>
              <label className="block text-xs font-bold uppercase text-slate-500">Nombre</label>
              <input
                value={agentName}
                onChange={(event) => setAgentName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
              />
              <label className="mt-4 block text-xs font-bold uppercase text-slate-500">Tono</label>
              <textarea
                value={tone}
                onChange={(event) => setTone(event.target.value)}
                rows={3}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
              />
              <label className="mt-4 block text-xs font-bold uppercase text-slate-500">
                Maximo de preguntas por mensaje
              </label>
              <input
                type="number"
                min="1"
                max="4"
                value={maxQuestions}
                onChange={(event) => setMaxQuestions(event.target.value)}
                className="mt-1 w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
              />
              <label className="mt-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <input
                  type="checkbox"
                  checked={hasGreeted}
                  onChange={(event) => setHasGreeted(event.target.checked)}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block text-sm font-black text-slate-950">
                    Este usuario ya fue saludado
                  </span>
                  <span className="mt-1 block text-xs font-bold leading-5 text-slate-500">
                    Si esta apagado, el agente agrega saludo. Si esta prendido, revisa cuantas horas pasaron.
                  </span>
                </span>
              </label>
              <label className="mt-4 block text-xs font-bold uppercase text-slate-500">
                Horas desde el ultimo mensaje
              </label>
              <input
                type="number"
                min="0"
                value={hoursSinceLastMessage}
                onChange={(event) => setHoursSinceLastMessage(event.target.value)}
                className="mt-1 w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
              />
              <p className="mt-1 text-xs font-bold leading-5 text-slate-500">
                Si pasaron 8 horas o mas, el agente saluda de nuevo aunque ya haya saludado antes.
              </p>
              <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-900">
                Esta version guarda cambios en este navegador. En el siguiente paso conectamos estos campos a Supabase para que tambien se guarden en cualquier computadora.
              </p>
              <button
                type="button"
                onClick={resetLocalChanges}
                className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600"
              >
                Reiniciar cambios locales
              </button>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <PencilLine size={20} className="text-orange-600" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-black text-slate-950">Preguntas del filtro</h2>
                  <p className="text-sm text-slate-600">
                    Datos que el agente debe conseguir poco a poco.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addQuestion}
                  className="shrink-0 rounded-lg border border-orange-200 bg-orange-500 px-3 py-2 text-xs font-black text-white shadow-sm"
                >
                  + Agregar pregunta
                </button>
              </div>
              <div className="space-y-2">
                {editableQuestions.map((question, index) => (
                  <div key={`question-${index}`} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-slate-400">Pregunta {index + 1}</p>
                      <button
                        type="button"
                        onClick={() => removeQuestion(index)}
                        className="text-xs font-black text-slate-500"
                      >
                        Quitar
                      </button>
                    </div>
                    <textarea
                      value={question}
                      onChange={(event) => updateQuestion(index, event.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-orange-300"
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <Bot size={20} className="text-blue-600" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-black text-slate-950">Respuestas base</h2>
                  <p className="text-sm text-slate-600">
                    Edita aqui lo que contesta el simulador. Se guarda en este navegador y se refleja al probar otra vez.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addCustomResponseTemplate}
                  className="shrink-0 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-black text-white shadow-sm"
                >
                  + Agregar respuesta
                </button>
              </div>

              <div className="space-y-4">
                {[
                  ["firstGreeting", "Saludo solo en primer contacto"],
                  ["returningGreeting", "Saludo cuando regresa despues de horas o dias"],
                  ["propertySearch", "Busca local, bodega, terreno u oficina"],
                  ["propertyInterest", "Pregunta por propiedad especifica"],
                  ["unknownProperty", "Pregunta sin saber que propiedad es"],
                  ["ownerPublish", "Dueno quiere publicar"],
                  ["imageReceived", "Manda imagen o captura"],
                  ["leadSummary", "Resumen ordenado de datos del interesado"],
                  ["unknown", "Mensaje no claro"],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="text-xs font-black uppercase text-slate-500">{label}</span>
                    <textarea
                      value={responseTemplates[key]}
                      onChange={(event) => updateTemplate(key, event.target.value)}
                      rows={key === "leadSummary" ? 9 : key === "propertyInterest" ? 4 : 3}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm leading-5 outline-none focus:border-orange-400"
                    />
                    {key === "propertyInterest" && (
                      <span className="mt-1 block text-xs font-bold text-slate-400">
                        Puedes usar {"{propertyTitle}"} para insertar el nombre de la propiedad.
                      </span>
                    )}
                    {key === "leadSummary" && (
                      <span className="mt-1 block text-xs font-bold leading-5 text-slate-400">
                        Variables disponibles: {"{spaceType}"}, {"{sizeRange}"}, {"{budget}"}, {"{zones}"}, {"{businessType}"}, {"{moveInDate}"}.
                      </span>
                    )}
                  </label>
                ))}
              </div>

              <div className="mt-5 border-t border-slate-100 pt-5">
                <p className="mb-3 text-xs font-black uppercase text-slate-500">
                  Respuestas base personalizadas
                </p>
                <div className="space-y-4">
                  {customResponseTemplates.map((item, index) => (
                    <div key={item.id || `custom-template-${index}`} className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-black uppercase text-blue-700">
                          Plantilla {index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeCustomResponseTemplate(index)}
                          className="text-xs font-black text-blue-700"
                        >
                          Quitar
                        </button>
                      </div>
                      <label className="block text-xs font-black uppercase text-blue-900">
                        Nombre de la plantilla
                      </label>
                      <input
                        value={item.label}
                        onChange={(event) =>
                          updateCustomResponseTemplate(index, "label", event.target.value)
                        }
                        className="mt-1 w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-blue-300"
                      />
                      <label className="mt-3 block text-xs font-black uppercase text-blue-900">
                        Respuesta
                      </label>
                      <textarea
                        value={item.template}
                        onChange={(event) =>
                          updateCustomResponseTemplate(index, "template", event.target.value)
                        }
                        rows={4}
                        className="mt-1 w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm leading-5 text-slate-800 outline-none focus:border-blue-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <CalendarClock size={20} className="text-blue-600" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-black text-slate-950">Seguimientos</h2>
                  <p className="text-sm text-slate-600">
                    Edita el tipo, tiempo y mensaje de seguimiento.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addFollowup}
                  className="shrink-0 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-black text-white shadow-sm"
                >
                  + Agregar
                </button>
              </div>
              <div className="space-y-3">
                {editableFollowups.map((rule, index) => (
                  <div key={`${rule.lead}-${index}`} className="rounded-xl border border-slate-100 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-black uppercase text-slate-400">
                        Seguimiento {index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeFollowup(index)}
                        className="text-xs font-black text-slate-500"
                      >
                        Quitar
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <input
                        value={rule.lead}
                        onChange={(event) => updateFollowup(index, "lead", event.target.value)}
                        className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-black text-slate-950 outline-none focus:border-blue-300"
                      />
                      <input
                        value={rule.time}
                        onChange={(event) => updateFollowup(index, "time", event.target.value)}
                        className="w-24 rounded-lg border border-blue-100 bg-blue-50 px-2 py-2 text-xs font-black text-blue-700 outline-none focus:border-blue-300"
                      />
                    </div>
                    <textarea
                      value={rule.message}
                      onChange={(event) => updateFollowup(index, "message", event.target.value)}
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs leading-5 text-slate-600 outline-none focus:border-blue-300"
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <Send size={18} className="text-emerald-600" />
                <h2 className="font-black text-slate-950">Cuando podras editarlo</h2>
              </div>
              <p className="text-sm leading-6 text-slate-600">
                Desde esta pantalla ya puedes ver la estructura. En la siguiente version se guardaran cambios en las tablas
                <b> whatsapp_agent_settings</b>, <b>whatsapp_agent_filter_questions</b> y <b>whatsapp_agent_followup_rules</b>.
              </p>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default AdminAgenteWhatsApp;
